import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Layers, Plus } from 'lucide-react';
import { deleteAgent, listAgents } from '../../api/agents';
import ConfirmModal from '../../components/ui/ConfirmModal';
import EmptyState from '../../components/ui/EmptyState';
import { useMultiAgentGate } from '../../hooks/useMultiAgentGate';
import { EMPTY_STATE_ICONS } from '../../support/iconMappings';
import { getApiErrorMessage } from '../../utils/errors';
import AgentStatusBadge from './components/AgentStatusBadge';
import AgentTableActionsMenu from './components/AgentTableActionsMenu';

const PER_PAGE = 20;

function ChannelCell({ agent }) {
  if (agent.channel?.phone_number) {
    return (
      <span className="text-content-secondary whitespace-nowrap truncate" title={agent.channel.phone_number}>
        {agent.channel.phone_number}
      </span>
    );
  }

  return (
    <Link
      to={`/channels?agentId=${agent.id}`}
      className="inline-flex items-center text-xs font-medium text-forge-600 hover:text-forge-700 underline decoration-forge-300 hover:decoration-forge-500 whitespace-nowrap"
      title="Conectar canal a este agente"
    >
      Conectar canal
    </Link>
  );
}

function formatLlmSummary(profile) {
  const model = profile?.model_line?.label;
  const effort = profile?.effort?.label;
  const parts = [model, effort].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function LlmProfileCell({ profile }) {
  const summary = formatLlmSummary(profile);
  const fullLabel = profile?.label || summary;

  if (!summary) {
    return <span className="text-content-subtle">—</span>;
  }

  return (
    <span
      className="block truncate text-content-secondary"
      title={fullLabel}
    >
      {summary}
    </span>
  );
}

function SubAgentsLink({ agent, compact = false }) {
  const count = agent.sub_agents_count ?? 0;
  const label =
    count > 0
      ? `${count} comportamento${count !== 1 ? 's' : ''}`
      : 'Adicionar comportamentos';

  if (count > 0) {
    return (
      <Link
        to={`/agents/${agent.id}/sub-agents`}
        title={label}
        aria-label={label}
        className={
          compact
            ? 'inline-flex items-center justify-center gap-1 min-w-[1.75rem] h-7 px-2 rounded-md text-xs font-semibold bg-forge-50 text-forge-700 border border-forge-200 hover:bg-forge-100 whitespace-nowrap'
            : 'inline-flex items-center gap-1.5 text-forge-700 hover:text-forge-800 font-medium whitespace-nowrap'
        }
      >
        <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {compact ? count : label}
      </Link>
    );
  }

  return (
    <Link
      to={`/agents/${agent.id}/edit`}
      title={label}
      aria-label={label}
      className={
        compact
          ? 'inline-flex items-center justify-center gap-1 min-w-[1.75rem] h-7 px-2 rounded-md text-xs text-content-subtle border border-dashed border-slate-300 hover:text-content-secondary hover:border-slate-400 whitespace-nowrap'
          : 'inline-flex items-center gap-1.5 text-content-subtle hover:text-content-secondary whitespace-nowrap'
      }
    >
      <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {compact ? '—' : 'Nenhum · adicionar'}
    </Link>
  );
}

function AgentsList() {
  const location = useLocation();
  const { isMultiAgent, refresh: refreshMultiAgentGate } = useMultiAgentGate();
  const successFromNav = location.state?.onboardingSuccess
    ? `Agente "${location.state.agentName}" criado com sucesso!`
    : location.state?.editSuccess
      ? `Agente "${location.state.agentName}" atualizado com sucesso!`
      : null;

  const [agents, setAgents] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(successFromNav);
  const [deactivatingAgent, setDeactivatingAgent] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const loadAgents = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');

    try {
      const response = await listAgents({ page, perPage: PER_PAGE });
      setAgents(response.data || []);
      setMeta(response.meta || { page, per_page: PER_PAGE, total: 0 });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os agentes.'));
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents(1);
  }, [loadAgents]);

  const handleConfirmDeactivate = async () => {
    if (!deactivatingAgent) {
      return;
    }

    setIsDeactivating(true);
    setError('');
    try {
      await deleteAgent(deactivatingAgent.id);
      const name = deactivatingAgent.name;
      setDeactivatingAgent(null);
      setSuccessMessage(`Agente "${name}" desativado.`);
      const nextPage =
        agents.length <= 1 && meta.page > 1 ? meta.page - 1 : meta.page;
      await loadAgents(nextPage);
      refreshMultiAgentGate?.();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível desativar o agente.'));
    } finally {
      setIsDeactivating(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Agentes</h1>
          <p className="text-content-muted text-sm mt-1">
            Agentes da sua conta e configurações de LLM.
          </p>
        </div>
        <Link to="/onboarding" className="btn-primary text-sm px-4 py-2.5 shrink-0">
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          Criar agente
        </Link>
      </div>

      {successMessage && (
        <p className="text-sm text-forge-700 bg-forge-50 border border-forge-200 rounded-xl px-4 py-3">
          {successMessage}
        </p>
      )}

      {isMultiAgent && (
        <div className="surface-card px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-content-secondary">
            Vários agentes ativos? Configure o roteamento inbound para direcionar
            mensagens ao agente certo.
          </p>
          <Link
            to="/inbound-routers"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-forge-700 hover:text-forge-800 shrink-0"
          >
            Ir para roteamento
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </Link>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{error}</p>
          <button type="button" onClick={() => loadAgents(meta.page)} className="btn-secondary text-sm px-3 py-2">
            Tentar novamente
          </button>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando agentes...</p>
        </div>
      ) : agents.length === 0 && !error ? (
        <EmptyState
          icon={EMPTY_STATE_ICONS.agents}
          title="Nenhum agente criado ainda"
          description="Comece pelo onboarding para definir tom de voz e comportamentos."
          action={(
            <Link to="/onboarding" className="btn-primary text-sm px-4 py-2.5">
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              Criar primeiro agente
            </Link>
          )}
        />
      ) : (
        <>
          <div className="hidden md:block surface-card overflow-hidden">
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="border-b border-surface-border bg-slate-50 text-left">
                  <th className="px-4 py-2 font-medium text-content-muted w-[34%]">Nome</th>
                  <th className="px-4 py-2 font-medium text-content-muted w-[10%]">Status</th>
                  <th className="px-4 py-2 font-medium text-content-muted w-[14%]">Canal</th>
                  <th
                    className="px-4 py-2 font-medium text-content-muted w-[8%] text-center"
                    title="Comportamentos"
                  >
                    Comp.
                  </th>
                  <th className="px-4 py-2 font-medium text-content-muted w-[26%]">LLM</th>
                  <th className="px-4 py-2 font-medium text-content-muted w-[8%] whitespace-nowrap text-right">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => (
                  <tr
                    key={agent.id}
                    className="border-b border-surface-border last:border-b-0 hover:bg-slate-50/80 transition-colors"
                  >
                    <td className="px-4 py-2 min-w-0">
                      <p
                        className="font-medium text-content-primary truncate"
                        title={`${agent.name}\n${agent.id}`}
                      >
                        {agent.name}
                      </p>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <AgentStatusBadge status={agent.status} compact />
                    </td>
                    <td className="px-4 py-2 text-content-secondary whitespace-nowrap truncate">
                      <ChannelCell agent={agent} />
                    </td>
                    <td className="px-4 py-2 text-center whitespace-nowrap">
                      <SubAgentsLink agent={agent} compact />
                    </td>
                    <td className="px-4 py-2 min-w-0">
                      <LlmProfileCell profile={agent.llm_profile} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <AgentTableActionsMenu
                        agentId={agent.id}
                        onDeactivate={() => setDeactivatingAgent(agent)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="surface-card p-4 space-y-2.5"
              >
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <p
                      className="font-medium text-content-primary truncate whitespace-nowrap"
                      title={`${agent.name}\n${agent.id}`}
                    >
                      {agent.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <AgentStatusBadge status={agent.status} compact />
                    <AgentTableActionsMenu
                      agentId={agent.id}
                      onDeactivate={() => setDeactivatingAgent(agent)}
                    />
                  </div>
                </div>
                <div className="text-sm text-content-muted space-y-1.5">
                  <p className="truncate">
                    <span className="text-content-subtle text-xs">LLM </span>
                    {formatLlmSummary(agent.llm_profile) || '—'}
                  </p>
                  <p className="truncate">
                    <span className="text-content-subtle text-xs">Canal: </span>
                    <ChannelCell agent={agent} />
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-content-subtle">Comp.:</span>
                    <SubAgentsLink agent={agent} compact />
                  </p>
                </div>
              </div>
            ))}
          </div>

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                {meta.total} agente{meta.total !== 1 ? 's' : ''} · página {meta.page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => loadAgents(meta.page - 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages || loading}
                  onClick={() => loadAgents(meta.page + 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        open={Boolean(deactivatingAgent)}
        title="Desativar agente"
        message={
          deactivatingAgent
            ? `Desativar o agente "${deactivatingAgent.name}"? Ele deixa de responder mensagens. O histórico de conversas é mantido.`
            : ''
        }
        confirmLabel="Desativar"
        confirmingLabel="Desativando..."
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setDeactivatingAgent(null)}
        isConfirming={isDeactivating}
      />
    </div>
  );
}

export default AgentsList;
